import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import type { AIAnalysis } from '../../../types/checkin';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Card, CardHeader, CardContent } from '../../ui/Card';
import {
  SparklesIcon,
  BookOpenIcon,
  MapPinIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface InsightsCompletionScreenProps {
  analysis: AIAnalysis;
  onDone: () => void;
}

// Risk level configuration
const riskConfig = {
  low: {
    emoji: '🌟',
    variant: 'success' as const,
    label: 'Low Risk',
    message: 'You\'re doing great! Keep up the good work.',
  },
  medium: {
    emoji: '⚠️',
    variant: 'warning' as const,
    label: 'Medium Risk',
    message: 'Some areas need attention. Stay mindful.',
  },
  high: {
    emoji: '🚨',
    variant: 'danger' as const,
    label: 'High Risk',
    message: 'Important: Consider reaching out for support.',
  },
  crisis: {
    emoji: '🆘',
    variant: 'danger' as const,
    label: 'Crisis',
    message: 'URGENT: Please reach out to your sponsor or call a helpline.',
  },
};

export function InsightsCompletionScreen({
  analysis,
  onDone,
}: InsightsCompletionScreenProps) {
  const navigate = useNavigate();
  const config = riskConfig[analysis.riskLevel];

  // Get top 3 feedback items
  const topInsights = analysis.feedback.slice(0, 3);

  // Get first Big Book citation
  const firstCitation = analysis.citations[0];

  // Determine if we should show "Find Meeting" button (high/crisis risk)
  const showFindMeeting =
    analysis.riskLevel === 'high' || analysis.riskLevel === 'crisis';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-surface-50 dark:bg-surface-900 px-4 py-8"
    >
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center">
          <div className="text-6xl mb-4">{config.emoji}</div>
          <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100 mb-2">
            Daily Review Complete
          </h1>
          <p className="text-surface-600 dark:text-surface-400">
            Here's your personalized recovery insight
          </p>
        </motion.div>

        {/* Risk Level Badge */}
        <motion.div variants={itemVariants} className="flex justify-center">
          <Badge variant={config.variant} size="lg">
            {config.label}
          </Badge>
        </motion.div>

        {/* Risk Message */}
        <motion.div variants={itemVariants}>
          <Card variant="elevated">
            <CardContent>
              <p className="text-center text-lg font-medium text-surface-800 dark:text-surface-200">
                {config.message}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Top Insights */}
        <motion.div variants={itemVariants}>
          <Card variant="default">
            <CardHeader
              title="Key Insights"
              action={<SparklesIcon className="w-6 h-6 text-accent-500" />}
            />
            <CardContent>
              <ul className="space-y-3">
                {topInsights.map((insight, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" />
                    <span className="text-surface-700 dark:text-surface-300">
                      {insight}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </motion.div>

        {/* Big Book Citation */}
        {firstCitation && (
          <motion.div variants={itemVariants}>
            <Card variant="filled">
              <CardHeader
                title="Big Book Wisdom"
                action={<BookOpenIcon className="w-6 h-6 text-primary-500" />}
              />
              <CardContent>
                <blockquote className="italic text-surface-700 dark:text-surface-300 border-l-4 border-primary-500 pl-4">
                  {firstCitation}
                </blockquote>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div variants={itemVariants} className="space-y-3">
          <Button
            variant="accent"
            size="lg"
            fullWidth
            onClick={() => navigate('/journal?view=insights')}
            leftIcon={<SparklesIcon className="w-5 h-5" />}
          >
            View Full Analysis
          </Button>

          {showFindMeeting && (
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => navigate('/meetings')}
              leftIcon={<MapPinIcon className="w-5 h-5" />}
            >
              Find a Meeting
            </Button>
          )}

          <Button variant="ghost" size="lg" fullWidth onClick={onDone}>
            Done
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default InsightsCompletionScreen;
